import React, { Fragment } from 'react';
import { TextField } from '@mui/material';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import OutlinedInput from '@mui/material/OutlinedInput';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Select from '@mui/material/Select';
import Typography from '@mui/material/Typography';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import TimePickerCustom from 'src/components/common/field/TimePickerCustom';
import GenerateFieldChild from './GenerateFieldChild';

const inputStyle = {
  fontSize: '16px',
  my: '8px',
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#BDBDBD',
  },
  '& .MuiInputBase-input': { padding: '8px 14px' },
  '& input::placeholder': {
    fontSize: '14px',
    fontWeight: 500,
    color: '#CCD1D9',
    opacity: 1,
  },
  backgroundColor: 'primary.light',
  width: '100%',
};

const dateStyle = {
  width: '100%',
  height: '32px',
  my: '8px',
  '& .MuiInputBase-input.MuiOutlinedInput-input': {
    py: '8px',
    fontSize: '14px',
  },
};

const colorRequired = {
  color: '#ED5565',
  fontSize: '14px',
  fontWeight: '500',
};

const GenerateField = (props: any) => {
  const field = props.field;

  const handleInputChange = (event: any, name: string) => {
    props.handleChangeValue(name, {
      value: [event.target.value],
    });
  };

  const handleCheckboxChange = (event: any, name: string) => {
    const { value, checked } = event.target;
    let tempAnswer: any = [];
    if (!props.answer?.value) {
      tempAnswer = [value];
    } else {
      if (checked) {
        tempAnswer = [...props.answer?.value, ...[value]];
      } else {
        // If unchecking the "その他" option, remove both "その他" and any "その他:value" entries
        if (value === 'その他') {
          tempAnswer = props.answer?.value.filter(
            (val: any) => val !== 'その他' && !val.startsWith('その他（')
          );
        }
        // If unchecking the "知人/友人からの紹介" option
        else if (value === '知人/友人からの紹介') {
          tempAnswer = props.answer?.value.filter(
            (val: any) =>
              val !== '知人/友人からの紹介' &&
              !val.startsWith('知人/友人からの紹介（')
          );
        }
        // If unchecking the "家族からの紹介" option
        else if (value === '家族からの紹介') {
          tempAnswer = props.answer?.value.filter(
            (val: any) =>
              val !== '家族からの紹介' && !val.startsWith('家族からの紹介（')
          );
        } else {
          tempAnswer = props.answer?.value.filter((val: any) => val !== value);
        }
      }
    }
    props.handleChangeValue(name, {
      value: tempAnswer,
    });
  };

  const handleRadioChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    name: string
  ) => {
    const { value } = event.target;
    props.handleChangeValue(name, {
      value: [value],
    });
  };

  const handleDateChange = (newValue: dayjs.Dayjs | null, name: string) => {
    props.handleChangeValue(name, {
      value: [newValue?.format('YYYY/MM/DD')],
    });
  };

  const handleSelectChange = (event: any, name: string) => {
    const { value } = event.target;
    props.handleChangeValue(name, {
      value: [value],
    });
  };

  const handleChangeTime = (name: string) => (date: Dayjs | null) => {
    const timeFormat = date ? date.format('HH:mm') : null;
    props.handleChangeValue(name, {
      value: [timeFormat],
    });
  };

  const handleChildChangeValue = (name: string, value: any) => {
    props.handleChangeValue(field.field_id, {
      ...props.answer,
      children_answers: {
        [name]: {
          dependent_answers: [''],
          value: [value],
        },
      },
    });
  };

  const handleChildDFChangeValue = (name: string, value: any) => {
    props.handleChangeValue(field.field_id, {
      ...props.answer,
      children_answers: {
        [name]: {
          value: props.answer?.children_answers
            ? [...props.answer?.children_answers[name].value]
            : [],
          dependent_answers: [value],
        },
      },
    });
  };

  const handleChildMultiChangeValue = (name: string, value: any) => {
    let dependentAnswers: any = [];
    if (props.answer?.children_answers && props.answer?.children_answers[name]?.dependent_answers) {
      const currentDependentAnswers = props.answer.children_answers[name].dependent_answers[0];
      if (currentDependentAnswers && typeof currentDependentAnswers === 'object') {
        const updatedDependentAnswers = { ...currentDependentAnswers };

        Object.keys(updatedDependentAnswers).forEach(key => {
          if (!value.includes(key)) {
            delete updatedDependentAnswers[key];
          }
        });
        
        dependentAnswers = [updatedDependentAnswers];
      }
    }
    props.handleChangeValue(field.field_id, {
      ...props.answer,
      children_answers: {
        [name]: {
          dependent_answers: props.answer?.children_answers
            ? dependentAnswers
            : [],
          value: value,
        },
      },
    });
  };

  const handleChildMultiDFChangeValue = (name: string, value: any) => {
    props.handleChangeValue(field.field_id, {
      ...props.answer,
      children_answers: {
        [name]: {
          value: props.answer?.children_answers
            ? [...props.answer?.children_answers[name].value]
            : [],
          dependent_answers: value,
        },
      },
    });
  };

  const cannotEdit = () => {
    return ((props.isEdit  && props.checkAnswer !== undefined && props.checkAnswer !== null) || props.staffEdit) && field.cannot_edit ? (
      <Typography fontSize={'12px'} mb={'8px'}>
        ※変更等がございましたら、お手数ですがクリニックまでご一報くださいませ。
      </Typography>
    ) : (
      <></>
    );
  };

  switch (field.type) {
    case 'text':
      return (
        <Box mb={'20px'}>
          <Typography fontWeight={700} mb={'8px'}>
            {field.label || '-'}
            {field.required && (
              <Typography component='label' {...colorRequired}>
                *
              </Typography>
            )}
          </Typography>
          <Typography fontSize={'12px'} mb={'8px'}>
            {field.help_text || ''}
          </Typography>
          {cannotEdit()}
          <OutlinedInput
            placeholder={`${field.label}を入力してください`}
            sx={inputStyle}
            inputProps={{
              sx: {
                backgroundColor: 'primary.light',
              },
            }}
            name={field.field_id}
            value={props.answer?.value}
            onChange={(event) => handleInputChange(event, field.field_id)}
            disabled={props.isEdit && field.cannot_edit && props.checkAnswer !== undefined && props.checkAnswer !== null}
          />
        </Box>
      );
    case 'textarea':
      return (
        <Box mb={'20px'}>
          <Typography fontWeight={700} mb={'8px'}>
            {field.label || '-'}
            {field.required && (
              <Typography component='label' {...colorRequired}>
                *
              </Typography>
            )}
          </Typography>
          <Typography fontSize={'12px'} mb={'8px'}>
            {field.help_text || ''}
          </Typography>
          {cannotEdit()}
          <TextField
            margin='dense'
            value={props.answer?.value}
            placeholder={`${field.label}を入力してください`}
            fullWidth
            multiline
            rows={field.rows || 4}
            onChange={(event) => handleInputChange(event, field.field_id)}
            sx={inputStyle}
            disabled={props.isEdit && field.cannot_edit && props.checkAnswer !== undefined && props.checkAnswer !== null}
          />
        </Box>
      );

    case 'checkbox':
      return (
        <Box mb={'20px'}>
          <Typography fontWeight={700} mb={'8px'}>
            {field.label || '-'}
            {field.required && (
              <Typography component='label' {...colorRequired}>
                *
              </Typography>
            )}
          </Typography>
          <Typography fontSize={'12px'} mb={'8px'}>
            {field.help_text || ''}
          </Typography>
          {cannotEdit()}
          <Grid container spacing={'8px'}>
            {field.options.map((option: any, idx: number) => {
              const isOtherOption = option === 'その他';
              const isFriendOption = option === '知人/友人からの紹介';
              const isFamilyOption = option === '家族からの紹介';
              // Ensure we have an array, even if empty
              const answerValues = Array.isArray(props.answer?.value)
                ? props.answer.value
                : [];

              // For "その他" option
              const otherValue = answerValues.find((val: string) =>
                val?.startsWith('その他（')
              );
              const otherInputValue = otherValue
                ? otherValue.substring(4, otherValue.length - 1)
                : '';
              const isOtherChecked = answerValues.some(
                (val: string) => val === 'その他' || val?.startsWith('その他（')
              );

              // For "知人/友人からの紹介" option
              const friendValue = answerValues.find((val: string) =>
                val?.startsWith('知人/友人からの紹介（')
              );
              const friendInputValue = friendValue
                ? friendValue.substring(11, friendValue.length - 1)
                : '';
              const isFriendChecked = answerValues.some(
                (val: string) =>
                  val === '知人/友人からの紹介' ||
                  val?.startsWith('知人/友人からの紹介（')
              );

              // For "家族からの紹介" option
              const familyValue = answerValues.find((val: string) =>
                val?.startsWith('家族からの紹介（')
              );
              const familyInputValue = familyValue
                ? familyValue.substring(8, familyValue.length - 1)
                : '';
              const isFamilyChecked = answerValues.some(
                (val: string) =>
                  val === '家族からの紹介' ||
                  val?.startsWith('家族からの紹介（')
              );
              return (
                <Fragment key={`${field.field_id}-${idx}`}>
                  <Grid item xs={6}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={
                            isOtherOption
                              ? Boolean(isOtherChecked)
                              : isFriendOption
                              ? Boolean(isFriendChecked)
                              : isFamilyOption
                              ? Boolean(isFamilyChecked)
                              : Boolean(answerValues.includes(option))
                          }
                          onChange={(event) =>
                            handleCheckboxChange(event, field.field_id)
                          }
                          value={option}
                        />
                      }
                      label={option}
                      sx={{
                        '& .MuiSvgIcon-root': { fontSize: '18px' },
                        '& .MuiTypography-root': {
                          fontSize: '14px',
                          wordBreak: 'break-word',
                        },
                      }}
                      disabled={props.isEdit && field.cannot_edit && props.checkAnswer !== undefined && props.checkAnswer !== null}
                    />
                  </Grid>
                  {isOtherOption && isOtherChecked && (
                    <Grid item xs={12}>
                      <OutlinedInput
                        placeholder='その他を入力してください'
                        sx={{
                          ...inputStyle,
                          width: '100%',
                        }}
                        value={otherInputValue || ''}
                        onChange={(e) => {
                          const newValue = e.target.value;
                          let updatedValues = [...(answerValues || [])];

                          // Remove any existing その他 entries
                          updatedValues = updatedValues.filter(
                            (val: string) =>
                              val !== 'その他' &&
                              !val?.startsWith('その他（') &&
                              !val?.startsWith('その他:')
                          );

                          // Add the new その他 entry with input value
                          updatedValues.push(`その他（${newValue}）`);

                          props.handleChangeValue(field.field_id, {
                            value: updatedValues,
                          });
                        }}
                        disabled={props.isEdit && field.cannot_edit && props.checkAnswer !== undefined && props.checkAnswer !== null}
                      />
                    </Grid>
                  )}

                  {/* Input field for "知人/友人からの紹介" option */}
                  {isFriendOption && isFriendChecked && (
                    <Grid item xs={12}>
                      <OutlinedInput
                        placeholder='知人/友人からの紹介を入力してください'
                        sx={{
                          ...inputStyle,
                          width: '100%',
                        }}
                        value={friendInputValue || ''}
                        onChange={(e) => {
                          const newValue = e.target.value;
                          let updatedValues = [...(answerValues || [])];

                          // Remove any existing 知人/友人からの紹介 entries
                          updatedValues = updatedValues.filter(
                            (val: string) =>
                              val !== '知人/友人からの紹介' &&
                              !val?.startsWith('知人/友人からの紹介（')
                          );

                          // Add the new 知人/友人からの紹介 entry with input value
                          updatedValues.push(
                            `知人/友人からの紹介（${newValue}）`
                          );

                          props.handleChangeValue(field.field_id, {
                            value: updatedValues,
                          });
                        }}
                        disabled={props.isEdit && field.cannot_edit && props.checkAnswer !== undefined && props.checkAnswer !== null}
                      />
                    </Grid>
                  )}

                  {/* Input field for "家族からの紹介" option */}
                  {isFamilyOption && isFamilyChecked && (
                    <Grid item xs={12}>
                      <OutlinedInput
                        placeholder='家族からの紹介を入力してください'
                        sx={{
                          ...inputStyle,
                          width: '100%',
                        }}
                        value={familyInputValue || ''}
                        onChange={(e) => {
                          const newValue = e.target.value;
                          let updatedValues = [...(answerValues || [])];

                          // Remove any existing 家族からの紹介 entries
                          updatedValues = updatedValues.filter(
                            (val: string) =>
                              val !== '家族からの紹介' &&
                              !val?.startsWith('家族からの紹介（')
                          );

                          // Add the new 家族からの紹介 entry with input value
                          updatedValues.push(`家族からの紹介（${newValue}）`);

                          props.handleChangeValue(field.field_id, {
                            value: updatedValues,
                          });
                        }}
                        disabled={props.isEdit && field.cannot_edit && props.checkAnswer !== undefined && props.checkAnswer !== null}
                      />
                    </Grid>
                  )}
                </Fragment>
              );
            })}
          </Grid>
        </Box>
      );
    case 'radio':
      return (
        <Box mb={'20px'}>
          <Typography fontWeight={700} mb={'8px'}>
            {field.label || '-'}
            {field.required && (
              <Typography component='label' {...colorRequired}>
                *
              </Typography>
            )}
          </Typography>
          <Typography fontSize={'12px'} mb={'8px'}>
            {field.help_text || ''}
          </Typography>
          {cannotEdit()}
          <RadioGroup
            // row
            aria-labelledby='demo-controlled-radio-buttons-group'
            name={field.field_id}
            value={props.answer?.value || ''}
            onChange={(e) => handleRadioChange(e, field.field_id)}
          >
            {field.options.map((option: any, idx: number) => {
              if (option === null) return <div key={idx}></div>;
              return (
                <div key={idx}>
                  <FormControlLabel
                    value={option}
                    control={<Radio disabled={props.isEdit && field.cannot_edit && props.checkAnswer !== undefined && props.checkAnswer !== null} />}
                    label={option}
                  />
                  {field.children_fields &&
                    field.children_fields.map((cf: any, cfIdx: number) => {
                      if (cf.parent_id !== idx) return <div key={cfIdx}></div>;
                      return (
                        <GenerateFieldChild
                          key={cfIdx}
                          field={cf}
                          labelParent={option}
                          answer={props.answer}
                          handleChildChangeValue={handleChildChangeValue}
                          handleChildMultiChangeValue={
                            handleChildMultiChangeValue
                          }
                          handleChildDFChangeValue={handleChildDFChangeValue}
                          handleChildMultiDFChangeValue={
                            handleChildMultiDFChangeValue
                          }
                          disabled={props.isEdit && field.cannot_edit && props.checkAnswer !== undefined && props.checkAnswer !== null}
                        />
                      );
                    })}
                </div>
              );
            })}
          </RadioGroup>
        </Box>
      );
    case 'date':
      return (
        <Box mb={'20px'}>
          <Typography fontWeight={700} mb={'8px'}>
            {field.label || '-'}
            {field.required && (
              <Typography component='label' {...colorRequired}>
                *
              </Typography>
            )}
          </Typography>
          <Typography fontSize={'12px'} mb={'8px'}>
            {field.help_text || ''}
          </Typography>
          {cannotEdit()}
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              name=''
              value={props.answer?.value ? dayjs(props.answer?.value) : null}
              onChange={(newValue) =>
                handleDateChange(newValue, field.field_id)
              }
              format='YYYY/MM/DD'
              sx={dateStyle}
            />
          </LocalizationProvider>
        </Box>
      );
    case 'time':
      return (
        <Box mb={'20px'}>
          <Typography fontWeight={700} mb={'8px'}>
            {field.label || '-'}
            {field.required && (
              <Typography component='label' {...colorRequired}>
                *
              </Typography>
            )}
          </Typography>
          <Typography fontSize={'12px'} mb={'8px'}>
            {field.help_text || ''}
          </Typography>
          {cannotEdit()}
          <TimePickerCustom
            value={dayjs(dayjs().format('YYYY-MM-DD') + ' ' + props.answer)}
            onChange={handleChangeTime(field.field_id)}
          />
        </Box>
      );
    case 'select':
      return (
        <Box mb={'20px'}>
          <Typography fontWeight={700} mb={'8px'}>
            {field.label || '-'}
            {field.required && (
              <Typography component='label' {...colorRequired}>
                *
              </Typography>
            )}
          </Typography>
          <Typography fontSize={'12px'} mb={'8px'}>
            {field.help_text || ''}
          </Typography>
          {cannotEdit()}

          <Select
            value={props.answer?.value || ' '}
            onChange={(e) => handleSelectChange(e, field.field_id)}
            sx={{
              backgroundColor: 'primary.light',
              width: '100%',
              '& .MuiInputBase-input.MuiOutlinedInput-input': {
                p: '6px 14px',
              },
            }}
            disabled={props.isEdit && field.cannot_edit && props.checkAnswer !== undefined && props.checkAnswer !== null}
          >
            <MenuItem value={' '}>
              {field.help_text || '選択してください'}
            </MenuItem>
            {field.options.map((option: any, idx: number) => {
              return (
                <MenuItem value={option} key={idx}>
                  {option}
                </MenuItem>
              );
            })}
          </Select>
        </Box>
      );

    default:
      return (
        <Box mb={'20px'}>
          <Typography fontWeight={700} mb={'8px'}>
            {field.label || '-'}
            {field.required && (
              <Typography component='label' {...colorRequired}>
                *
              </Typography>
            )}
          </Typography>
          <Typography fontSize={'12px'} mb={'8px'}>
            {field.help_text || ''}
          </Typography>
          {cannotEdit()}
          <OutlinedInput
            placeholder={`${field.label}を入力してください`}
            sx={inputStyle}
            inputProps={{
              sx: {
                backgroundColor: 'primary.light',
              },
            }}
            name={field.field_id}
            value={props.answer?.value}
            onChange={(event) => handleInputChange(event, field.field_id)}
            disabled={props.isEdit && field.cannot_edit && props.checkAnswer !== undefined && props.checkAnswer !== null}
          />
        </Box>
      );
  }
};

export default GenerateField;
